import { createTheme, Theme, Components } from '@mui/material/styles';
import { ThemeOptions } from '@mui/material/styles';
import { colors, darkColors, typography, spacing, borderRadius, shadows } from './colors';

// Extend the PaletteOptions interface to include our custom properties
declare module '@mui/material/styles' {
  interface Palette {
    status: {
      success: string;
      warning: string;
      error: string;
      info: string;
    };
    dashboard: {
      firstRowBg: string;
      secondRowBg: string;
    };
    gradients: {
      authDark: string;
      authLight: string;
    };
    custom: {
      primary: typeof colors.primary;
      secondary: typeof colors.secondary;
      text: typeof colors.text;
      background: typeof colors.background;
      border: typeof colors.border;
      dashboard: typeof colors.dashboard;
      gradients: typeof colors.gradients;
    };
  }
  interface PaletteOptions {
    status?: {
      success?: string;
      warning?: string;
      error?: string;
      info?: string;
    };
    dashboard?: {
      firstRowBg?: string;
      secondRowBg?: string;
    };
    gradients?: {
      authDark?: string;
      authLight?: string;
    };
    custom?: {
      primary?: typeof colors.primary;
      secondary?: typeof colors.secondary;
      text?: typeof colors.text;
      background?: typeof colors.background;
      border?: typeof colors.border;
      dashboard?: typeof colors.dashboard;
      gradients?: typeof colors.gradients;
    };
  }
}

const baseTheme: Partial<ThemeOptions> = {
  typography: {
    fontFamily: typography.fontFamily,
    h1: {
      fontSize: typography.fontSizes['4xl'],
      fontWeight: typography.fontWeights.bold,
    },
    h2: {
      fontSize: typography.fontSizes['3xl'],
      fontWeight: typography.fontWeights.semiBold,
    },
    h3: {
      fontSize: typography.fontSizes['2xl'],
      fontWeight: typography.fontWeights.semiBold,
    },
    h4: {
      fontSize: typography.fontSizes.xl,
      fontWeight: typography.fontWeights.medium,
    },
    h5: {
      fontSize: typography.fontSizes.lg,
      fontWeight: typography.fontWeights.medium,
    },
    h6: {
      fontSize: typography.fontSizes.base,
      fontWeight: typography.fontWeights.medium,
    },
    body1: {
      fontSize: typography.fontSizes.base,
      fontWeight: typography.fontWeights.regular,
    },
    body2: {
      fontSize: typography.fontSizes.sm,
      fontWeight: typography.fontWeights.regular,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: typography.fontFamily,
          borderRadius: '30px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          padding: '12px 24px',
          '&.MuiButton-contained': {
            backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
            color: theme.palette.mode === 'dark' ? '#191919' : '#ffffff',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#f0f0f0' : '#2A5A8A',
              color: theme.palette.mode === 'dark' ? '#191919' : '#ffffff',
            },
          },
          '&.MuiButton-outlined': {
            borderColor: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
            color: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
            '&:hover': {
              borderColor: theme.palette.mode === 'dark' ? '#f0f0f0' : '#2A5A8A',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(27, 70, 114, 0.1)',
            },
          },
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: `${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'}`,
          '&.MuiDialog-paper': {
            backgroundColor: `${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'}`,
          },
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: typography.fontFamily,
          '& .MuiOutlinedInput-root': {
            borderRadius: '30px',
            fontFamily: typography.fontFamily,
            backgroundColor: theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff',
            color: '#A6A6A6',
            '& fieldset': {
              borderColor: theme.palette.mode === 'dark' ? 'transparent' : '#E2E8F0',
              borderWidth: theme.palette.mode === 'dark' ? '0' : '1px',
            },
            '&:hover fieldset': {
              borderColor: theme.palette.mode === 'dark' ? 'transparent' : '#CBD5E1',
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.mode === 'dark' ? 'transparent' : '#1B4672',
            },
            '& input': {
              color: '#A6A6A6',
              fontSize: '1rem',
              padding: '12px 16px',
              backgroundColor: 'transparent',
              '&::placeholder': {
                color: '#A6A6A6',
                opacity: 1,
              },
              '&:-webkit-autofill': {
                backgroundColor: `${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'}`,
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'} inset`,
                WebkitTextFillColor: '#A6A6A6',
                color: '#A6A6A6',
                transition: 'background-color 5000s ease-in-out 0s',
              },
              '&:-webkit-autofill:hover': {
                backgroundColor: `${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'}`,
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'} inset`,
                WebkitTextFillColor: '#A6A6A6',
                color: '#A6A6A6',
              },
              '&:-webkit-autofill:focus': {
                backgroundColor: `${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'}`,
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'} inset`,
                WebkitTextFillColor: '#A6A6A6',
                color: '#A6A6A6',
              },
              '&:-webkit-autofill:active': {
                backgroundColor: `${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'}`,
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'} inset`,
                WebkitTextFillColor: '#A6A6A6',
                color: '#A6A6A6',
              },
            },
          },
          '& .MuiInputLabel-root': {
            fontFamily: typography.fontFamily,
            color: '#A6A6A6',
            '&.Mui-focused': {
              color: '#A6A6A6',
            },
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: typography.fontFamily,
          borderRadius: '30px',
          backgroundColor: theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff',
          color: '#A6A6A6',
          '& input': {
            color: '#A6A6A6',
            fontSize: '1rem',
            padding: '12px 16px',
            backgroundColor: 'transparent',
            '&::placeholder': {
              color: '#A6A6A6',
              opacity: 1,
            },
            '&:-webkit-autofill': {
              backgroundColor: `${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'}`,
              WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'} inset`,
              WebkitTextFillColor: '#A6A6A6',
              color: '#A6A6A6',
            },
            '&:-webkit-autofill:hover': {
              backgroundColor: `${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'}`,
              WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'} inset`,
              WebkitTextFillColor: '#A6A6A6',
              color: '#A6A6A6',
            },
            '&:-webkit-autofill:focus': {
              backgroundColor: `${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'}`,
              WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'} inset`,
              WebkitTextFillColor: '#A6A6A6',
              color: '#A6A6A6',
            },
            '&:-webkit-autofill:active': {
              backgroundColor: `${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'}`,
              WebkitBoxShadow: `0 0 0 1000px ${theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff'} inset`,
              WebkitTextFillColor: '#A6A6A6',
              color: '#A6A6A6',
            },
          },
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
        label: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: typography.fontFamily,
          borderRadius: '30px',
          backgroundColor: theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff',
          color: '#A6A6A6',
          '& fieldset': {
            borderColor: theme.palette.mode === 'dark' ? 'transparent' : '#E2E8F0',
            borderWidth: theme.palette.mode === 'dark' ? '0' : '1px',
          },
          '&:hover fieldset': {
            borderColor: theme.palette.mode === 'dark' ? 'transparent' : '#CBD5E1',
          },
          '&.Mui-focused fieldset': {
            borderColor: theme.palette.mode === 'dark' ? 'transparent' : '#1B4672',
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: borderRadius.lg,
          boxShadow: shadows.md,
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: `${spacing.md}`,
          fontFamily: typography.fontFamily,
          '&.MuiCardContent-root': {
            padding: `${spacing.md}`,
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: borderRadius.lg,
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
        primary: {
          fontFamily: typography.fontFamily,
        },
        secondary: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
        label: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
      },
    },
  },
};

export const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
      contrastText: colors.primary.contrast,
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
      contrastText: colors.secondary.contrast,
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      disabled: colors.text.disabled,
    },
    status: {
      success: colors.status.success,
      warning: colors.status.warning,
      error: colors.status.error,
      info: colors.status.info,
    },
    dashboard: {
      firstRowBg: colors.dashboard.firstRowBg,
      secondRowBg: colors.dashboard.secondRowBg,
    },
    gradients: {
      authDark: colors.gradients.authDark,
      authLight: colors.gradients.authLight,
    },
    custom: {
      primary: colors.primary,
      secondary: colors.secondary,
      text: colors.text,
      background: colors.background,
      border: colors.border,
      dashboard: colors.dashboard,
      gradients: colors.gradients,
    },
  },
  components: {
    ...baseTheme.components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: typography.fontFamily,
        },
        ':root': {
          '--dialog-bg-color': colors.primary.main,
          '--scrollbar-thumb': '#1B4672',
          '--scrollbar-track': 'rgba(27, 70, 114, 0.1)',
        },
        // Light mode scrollbar styling
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: 'rgba(27, 70, 114, 0.1)',
          borderRadius: '10px',
        },
        '::-webkit-scrollbar-thumb': {
          background: '#1B4672',
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.3s ease',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: '#2A5A8A',
          transform: 'scale(1.05)',
        },
        '::-webkit-scrollbar-corner': {
          background: 'rgba(27, 70, 114, 0.1)',
          borderRadius: '10px',
        },
        // Firefox scrollbar styling for light mode
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: '#1B4672 rgba(27, 70, 114, 0.1)',
        },
      },
    },
  },
} as any);

export const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: darkColors.primary.main,
      light: darkColors.primary.light,
      dark: darkColors.primary.dark,
      contrastText: darkColors.primary.contrast,
    },
    secondary: {
      main: darkColors.secondary.main,
      light: darkColors.secondary.light,
      dark: darkColors.secondary.dark,
      contrastText: darkColors.secondary.contrast,
    },
    background: {
      default: darkColors.background.default,
      paper: darkColors.background.paper,
    },
    text: {
      primary: darkColors.text.primary,
      secondary: darkColors.text.secondary,
      disabled: darkColors.text.disabled,
    },
    status: {
      success: darkColors.status.success,
      warning: darkColors.status.warning,
      error: darkColors.status.error,
      info: darkColors.status.info,
    },
    dashboard: {
      firstRowBg: darkColors.dashboard.firstRowBg,
      secondRowBg: darkColors.dashboard.secondRowBg,
    },
    gradients: {
      authDark: darkColors.gradients.authDark,
      authLight: darkColors.gradients.authLight,
    },
    custom: {
      primary: darkColors.primary,
      secondary: darkColors.secondary,
      text: darkColors.text,
      background: darkColors.background,
      border: darkColors.border,
      dashboard: darkColors.dashboard,
      gradients: darkColors.gradients,
    },
  },
  components: {
    ...baseTheme.components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: typography.fontFamily,
        },
        ':root': {
          '--dialog-bg-color': darkColors.primary.main,
          '--scrollbar-thumb': '#FFFFFF',
          '--scrollbar-track': 'rgba(255, 255, 255, 0.1)',
        },
        // Dark mode scrollbar styling
        '::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '::-webkit-scrollbar-track': {
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '10px',
        },
        '::-webkit-scrollbar-thumb': {
          background: '#FFFFFF',
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.3s ease',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(255, 255, 255, 0.9)',
          transform: 'scale(1.05)',
        },
        '::-webkit-scrollbar-corner': {
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '10px',
        },
        // Firefox scrollbar styling for dark mode
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: '#FFFFFF rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
} as any); 
